import React, { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { cx, css } from '@emotion/css';
import { CheckOutlined } from '@ant-design/icons';

export interface Props extends Omit<HTMLAttributes<HTMLLabelElement>, 'onChange'> {
  checked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
}

function CheckContainer(props: Props) {
  const {
    disabled = false,
    checked: checkedProp,
    onChange: onChangeProp,
    children,
    className,
    ...otherProps
  } = props;

  const inputId = useRef(`${Math.random()}`);

  const [checkedState, setCheckedState] = useState(checkedProp || false);

  const checked = checkedProp === undefined ? checkedState : checkedProp;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;

      if (onChangeProp) {
        onChangeProp(e.target.checked, e);
      }

      setCheckedState(e.target.checked);
    },
    [disabled, onChangeProp],
  );

  useEffect(() => {
    if (checkedProp !== undefined) {
      setCheckedState(checkedProp);
    }
  }, [checkedProp]);

  return (
    <Container
      checked={checked}
      htmlFor={inputId.current}
      className={cx([className, disabled ? CheckContainerDis : ''])}
      {...otherProps}
    >
      <input id={inputId.current} type="checkbox" checked={checked} onChange={handleChange} />
      <span>
        <CheckOutlined />
      </span>
      {children}
    </Container>
  );
}

export default CheckContainer;

const Container = styled.label<{ checked: boolean }>`
  display: inline-block;
  position: relative;
  padding: 10px;
  border: solid 1px var(--fett-border-strong);
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease-out;
  border-color: ${({ checked }) =>
    checked ? 'var(--ant-primary-color) !important' : 'var(--fett-border-strong)'};
  > input[type='checkbox'] {
    position: absolute;
    clip: rect(0, 0, 0, 0);
  }
  > input[type='checkbox'] + span {
    display: inline-block;
    position: absolute;
    bottom: 0;
    right: 0;
    width: 16px;
    height: 16px;
    border-style: solid;
    border-width: 8px;
    border-color: transparent var(--ant-primary-color) var(--ant-primary-color) transparent;
    color: #fff;
    opacity: ${({ checked }) => (checked ? 1 : 0)};
    transform: ${({ checked }) => (checked ? 'scale(1)' : 'scale(0)')};
    transform-origin: right bottom;
    transition: all 0.3s ease-out;
    > span {
      position: absolute;
      top: -2px;
      left: -2px;
      font-size: 10px;
    }
  }
`;

const CheckContainerDis = css`
  background: var(--fett-bg-app);
  color: var(--fett-text-quaternary);
  cursor: not-allowed !important;
`;
